import React, { useState } from 'react';
import { useBreakpoint } from '../useBreakpoint';
import { color, font, radius, space, text, weight } from '../theme';
import { Card } from './Card';
import TabBar from './TabBar';
import { StatTile } from './StatTile';
import Chip from './Chip';

/* The metrics card above an MK list (party page, the Knesset's MK tab). Owner's option A,
   2026-10-10:
   - a segmented control picks a group of metrics; each group shows up to 3 tiles of the
     aggregate figures on a fixed 3-slot grid, so the card never changes height;
   - a tile sorts the list by its metric, high to low; tapping it again flips the
     direction (the tile's arrow shows which way);
   - filters are a separate row of compact chips that wraps, so every filter stays in view,
     and combine with any sort.
   The page owns the state (sort, direction, filter) and the list; this card only draws
   the controls. */

export interface MetricGroup<S extends string> {
  key: string;
  label: string;
  keys: S[];
}

export interface MetricFilter<F extends string> {
  key: F;
  label: string;
  count: number;
}

interface MetricsCardProps<S extends string, F extends string> {
  title: string;
  groups: MetricGroup<S>[];
  labels: Record<S, string>;
  // The aggregate shown on each tile; null shows "—" and the tile can't be pressed
  values: Record<S, React.ReactNode | null>;
  sort: S | null;
  dir: 'asc' | 'desc';
  onSort: (key: S) => void;
  filters: MetricFilter<F>[];
  filter: F;
  onFilter: (key: F) => void;
}

function MetricsCard<S extends string, F extends string>({ title, groups, labels, values, sort, dir, onSort, filters, filter, onFilter }: MetricsCardProps<S, F>) {
  const { isDesktop } = useBreakpoint();
  // Start on the sorted metric's group, else the middle of the card's purpose: activity
  const initial = (sort && groups.find(g => g.keys.indexOf(sort) >= 0)) || groups.find(g => g.key === 'activity') || groups[0];
  const [groupKey, setGroupKey] = useState(initial.key);
  const group = groups.find(g => g.key === groupKey) || groups[0];
  const labelId = `metrics-filter-${title.replace(/\s+/g, '-')}`;

  return (
    <Card>
      {/* In a narrow column the hint drops below the title rather than squeezing it onto two lines */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: `${space.xxs}px ${space.sm}px`, marginBottom: space.md }}>
        <h2 style={text.heading}>{title}</h2>
        <span style={{ fontSize: font.caption, color: color.textSecondary }}>הקשה על מדד ממיינת את הרשימה</span>
      </div>
      {/* One group needs no switch */}
      {groups.length > 1 && <TabBar label="קבוצת מדדים" tabs={groups.map(g => ({ key: g.key, label: g.label }))} active={group.key} onChange={setGroupKey} />}
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(3, group.keys.length)}, minmax(0, 1fr))`, gap: space.sm, marginTop: groups.length > 1 ? space.sm + 2 : 0 }}>
        {group.keys.map(k => {
          const value = values[k];
          return (
            <StatTile
              key={k}
              variant="flat"
              value={value !== null && value !== undefined ? value : '—'}
              label={labels[k]}
              selected={sort === k}
              indicator={dir === 'desc' ? 'desc' : 'asc'}
              onClick={value !== null && value !== undefined ? () => onSort(k) : undefined}
            />
          );
        })}
        {group.keys.length < 3 && <span aria-hidden="true" style={{ borderRadius: radius.lg, border: `1.5px dashed ${color.border}` }} />}
      </div>

      {/* Filters: all in view, compact. Phone: the label on its own line, so the chips get the
          card's full width (two rows at most). Desktop: the label beside one row of chips. */}
      <div style={{
        borderTop: `1px solid ${color.divider}`, marginTop: space.md, paddingTop: space.sm,
        display: 'flex', flexDirection: isDesktop ? 'row' : 'column', alignItems: isDesktop ? 'flex-start' : 'stretch', gap: isDesktop ? space.sm : 0,
      }}>
        <span id={labelId} style={{ flexShrink: 0, lineHeight: isDesktop ? '44px' : 1.6, fontSize: font.small, fontWeight: weight.bold, color: color.textSecondary }}>סינון</span>
        <div role="group" aria-labelledby={labelId} style={{ flex: 1, minWidth: 0, display: 'flex', flexWrap: 'wrap', columnGap: space.xs + 2 }}>
          {filters.map(f => (
            <Chip key={f.key} size="sm" variant="flat" selected={filter === f.key} onClick={() => onFilter(f.key)} count={f.count}>
              {f.label}
            </Chip>
          ))}
        </div>
      </div>
    </Card>
  );
}

export default MetricsCard;
